import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { presignedURLGet, downloadFolders } from '../../api/services';
import { useNavbarContext } from 'context/NavbarContext';
import { uuidv4 } from 'common/uuidGenerator';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useActivityLogDownload } from 'hooks/useActivityLogDownload';

export default function useFileDownload() {
  const { downloadFile: downloadAs, addFolderDownload } = useNavbarContext();
  const location = useLocation();
  const { addToast } = useToast();
  const { addDownloadActivity } = useActivityLogDownload();

  const downloadFile = async (file, callback) => {
    let file_path = file?.file_path;
    let file_name = file?.display_name ? file?.display_name : location?.state?.case_details?.display_name;
    let version_id = file?.version_id;
    let file_sk = file?.sk;
    let file_pk = file?.pk;

    try {
      let data = await presignedURLGet(file_path, version_id, false, false, file_sk, file_pk);
      let url = data?.data?.file_url;
      downloadAs(url, file_name);

      addDownloadActivity({
        event_subtype: 'download_document',
        message: `Downloaded file: ${file_name}`,
        pk: file_pk,
        sk: file_sk,
        event_items: {
          case_id: file?.case_id ?? '',
          case_name: file?.case_name ?? '',
          is_lead: file?.is_lead,
        },
      });

      if (callback && typeof callback === 'function') {
        callback();
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, error.message || 'Failed to download file.');
    }
  };

  const downloadCertFile = async (file, callback) => {
    let file_path = file?.esignature_request?.cirtificate_url;
    let file_name = file?.display_name || file?.esignature_request?.request_name;
    let version_id = '';
    let data = await presignedURLGet(file_path, version_id);
    let url = data?.data?.file_url;
    downloadAs(url, file_name);

    addDownloadActivity({
      event_subtype: 'download_certificate',
      message: `Downloaded Certificate: ${file_name}`,
      event_items: {},
    });

    if (callback && typeof callback === 'function') {
      callback();
    }
  };

  const downloadFolder = async (folder, caseId) => {
    try {
      const downloadId = uuidv4();
      const folderData = {
        id: downloadId,
        folderName: folder?.display_name,
        folderId: folder?.folder_id,
        caseId: caseId,
        status: 'In Progress',
        progress: 0,
        zipFile: null,
        error: null,
        pk: folder?.pk,
        sk: folder?.sk,
        case_name: folder?.case_name,
        is_lead: folder?.is_lead,
      };

      // Add to folder downloads context
      addFolderDownload(folderData);

      const requestData = {
        sk: folder?.sk,
        folder_name: folder?.display_name,
        case_id: caseId,
      };

      // folder download API
      const response = await downloadFolders(requestData);
    } catch (error) {
      console.error('Error downloading folder:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to initiate folder download.');
      addFolderDownload({
        id: uuidv4(),
        folderName: folder?.display_name,
        folderId: folder?.folder_id,
        caseId: caseId,
        status: 'failed',
        error: error.message || 'Failed to initiate folder download',
      });
    }
  };

  return { downloadFile, downloadCertFile, downloadFolder };
}
